import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { GlobalStateService } from '../global-state.service';
import { environment } from 'src/environments/environment';
import { IRetryPolicy } from '@microsoft/signalr/dist/esm/IRetryPolicy';
import { SignalRLogger } from './signalr-logger.service';
import { BehaviorSubject } from 'rxjs';

// An API restart drops every viewer at once; spreading their catch-up reads keeps them from exhausting the DB pool together.
export const RECONNECT_CATCHUP_SPREAD_MS = 15000;

// Single source of truth for connection state.
export enum SignalRConnectionState {
    Disconnected = 'Disconnected',
    Connecting = 'Connecting',
    Connected = 'Connected',
    Reconnecting = 'Reconnecting'
}

@Injectable({ providedIn: 'root' })

export class DataSyncService {
    hubConnection: signalR.HubConnection = undefined as any;

    private connectionState$ = new BehaviorSubject<SignalRConnectionState>(SignalRConnectionState.Disconnected);
    public readonly connectionState = this.connectionState$.asObservable();

    get isConnected(): boolean {
        return this.connectionState$.value === SignalRConnectionState.Connected;
    }

    // Exposed so the HTTP interceptor can stamp every mutation with its originating connection,
    // letting the backend skip echoing onClientPortalChanges back to the tab that made the change.
    get connectionId(): string | null {
        return this.hubConnection?.connectionId ?? null;
    }

    // Held for as long as the connection lives, so a background tab keeps its WebSocket.
    private webLockResolver: (() => void) | null = null;

    private freshnessCheckInterval: ReturnType<typeof setInterval> | null = null;

    private currentGroupId: string = '';
    private reconnectCatchUpSeq = 0;
    private lastActiveTimestamp: number = Date.now();
    private eventListenersRegistered: boolean = false;

    // Interruptible sleep for Layer 2 — online/visibility events resolve this to wake up tryReconnect immediately
    private reconnectWakeup: (() => void) | null = null;

    // Set when the tab was hidden long enough that it has probably missed updates.
    private _sessionStale: boolean = false;
    get isSessionStale(): boolean { return this._sessionStale; }
    clearStaleFlag(): void { this._sessionStale = false; }

    private beforeUnloadHandler = () => {
        this.hubConnection?.stop();
    };

    constructor(
        private globalService: GlobalStateService,
        private signalRLogger: SignalRLogger,
    ) {}

    // Every transition goes through here, so the logs and the UI indicator cannot drift apart.
    private setConnectionState(state: SignalRConnectionState): void {
        const previous = this.connectionState$.value;
        if (previous === state) return;
        this.signalRLogger.stateChange(previous, state);
        this.connectionState$.next(state);
        // GlobalStateService drives the UI disconnection indicator.
        this.globalService.setWebsocketConnectionState({
            connected: state === SignalRConnectionState.Connected
        });
    }

    ConnectClient(Id: any) {
        if (!this.isConnected && this.connectionState$.value !== SignalRConnectionState.Connecting) {
            this.currentGroupId = Id;

            // Tells a first connection apart from one after a refresh, for the connection logs.
            const trigger = sessionStorage.getItem('signalr_was_connected') === '1' ? 'page_refresh' : 'login';

            this.setConnectionState(SignalRConnectionState.Connecting);
            const api_url = environment.api_url;

            // Drop the previous connection's handler so events are not delivered twice.
            if (this.hubConnection) {
                this.hubConnection.off('onClientPortalChanges');
            }

            this.hubConnection = new signalR.HubConnectionBuilder()
                .withUrl(`${api_url}synchub`)
                .withAutomaticReconnect(new SignalRConnectionRetryPolicy(this.signalRLogger))
                .withStatefulReconnect()
                .withServerTimeout(1000 * 60)
                .withKeepAliveInterval(1000 * 15)
                .build();

            this.hubConnection.start().then(async () => {
                const transport = (this.hubConnection as any).connection?.transport?.name
                    ?? (this.hubConnection as any)._connection?.transport?.name
                    ?? 'unknown';
                this.signalRLogger.connected(Id, this.hubConnection?.connectionId, transport, trigger);
                sessionStorage.setItem('signalr_was_connected', '1');
                await this.addGroup(Id);
                this.setConnectionState(SignalRConnectionState.Connected);
                // Web Lock keeps the socket alive once the tab goes to the background.
                this.acquireWebLock();
                this.startFreshnessCheck();
                this.registerEventListeners();
                window.addEventListener('beforeunload', this.beforeUnloadHandler);
            }).catch((err: any) => {
                this.signalRLogger.error('connection start failed', { groupId: Id, error: err?.message ?? String(err) });
                this.setConnectionState(SignalRConnectionState.Disconnected);
                setTimeout(() => this.tryReconnect(Id), 3000);
            });

            this.hubConnection.on('onClientPortalChanges', () => {
                this.signalRLogger.eventReceived('onClientPortalChanges');
                this.globalService.setClientPortalRelatedDataChanges();
            });

            this.hubConnection.onreconnected(async (connectionId) => {
                this.signalRLogger.reconnected(Id, connectionId, undefined, 'layer1');
                await this.onAfterReconnect(Id);
            });

            this.hubConnection.onreconnecting((err) => {
                this.signalRLogger.layer1Started(err, this.hubConnection?.connectionId);
                this.setConnectionState(SignalRConnectionState.Reconnecting);
            });

            this.hubConnection.onclose(res => {
                const timeSinceLastFreshness = Date.now() - this.lastFreshnessCheckAt;
                this.signalRLogger.disconnected('connection closed', res);
                this.signalRLogger.stateChange('onclose', `timeSinceLastFreshnessMs: ${timeSinceLastFreshness}, error: ${res?.message ?? 'none'}`);
                this.setConnectionState(SignalRConnectionState.Disconnected);
                this.tryReconnect(Id);
            });
        }
    }

    // A hub invoke can hang indefinitely, so race every call against a client-side timeout.
    private invokeWithTimeout<T>(method: string, ...args: any[]): Promise<T> {
        const timeout = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error(`Hub invoke '${method}' client-side timeout after 10s`)), 10000)
        );
        return Promise.race([this.hubConnection.invoke<T>(method, ...args), timeout]);
    }

    // Layer 3 reconnects the moment the browser regains network, Layer 4 when the tab is shown
    // again, so recovery does not wait for the Layer 2 backoff timer.
    private registerEventListeners(): void {
        if (this.eventListenersRegistered) return;
        this.eventListenersRegistered = true;

        window.addEventListener('online', this.onNetworkOnline);

        document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    private onNetworkOnline = (): void => {
        if (!this.isConnected && this.hubConnection && this.currentGroupId) {
            this.signalRLogger.networkOnlineRecovery();
            // Wake up Layer 2 sleep if it's waiting, otherwise start a new reconnect loop
            if (this.reconnectWakeup) {
                this.reconnectWakeup();
            } else {
                this.tryReconnect(this.currentGroupId);
            }
        }
    };

    private onVisibilityChange = (): void => {
        if (document.visibilityState === 'visible') {
            const dormantDuration = Date.now() - this.lastActiveTimestamp;

            if (!this.isConnected && this.hubConnection && this.currentGroupId) {
                // Dormant and disconnected for this long means updates were almost certainly missed.
                if (dormantDuration > 10 * 60 * 1000) {
                    this._sessionStale = true;
                }
                this.signalRLogger.visibilityRecovery(dormantDuration);
                // Wake up Layer 2 sleep if it's waiting, otherwise start a new reconnect loop
                if (this.reconnectWakeup) {
                    this.reconnectWakeup();
                } else {
                    this.tryReconnect(this.currentGroupId);
                }
            } else if (this.isConnected && dormantDuration > 10 * 60 * 1000) {
                // Still connected, but a long hidden stretch can silently miss updates, so refetch.
                this._sessionStale = true;
                this.signalRLogger.visibilityRecovery(dormantDuration);
                this.globalService.checkOutDatedData();
            }

            this.lastActiveTimestamp = Date.now();
        } else {
            this.lastActiveTimestamp = Date.now();
        }
    };

    async addGroup(Id: any) {
        try {
            await this.invokeWithTimeout('AddToGroup', Id);
            this.signalRLogger.groupJoined(Id, this.hubConnection?.connectionId);
        } catch (err) {
            this.signalRLogger.groupJoinFailed(Id, err);
        }
    }

    async tryReconnect(Id: string): Promise<void> {
        if (this.connectionState$.value === SignalRConnectionState.Reconnecting) {
            this.signalRLogger.stateChange('tryReconnect', 'skipped — already reconnecting');
            return;
        }
        this.signalRLogger.stateChange('tryReconnect', `starting — hubState: ${this.hubConnection?.state}`);
        this.setConnectionState(SignalRConnectionState.Reconnecting);

        // Retry forever: at the 60s cap that costs one attempt per minute, and the online and
        // visibility listeners can wake the sleep to retry sooner.
        for (let attempt = 0; ; attempt++) {
            const previousConnectionId = this.hubConnection?.connectionId;
            const delay = Math.min(3000 * Math.pow(2, attempt), 60000) + Math.random() * 1000;
            this.signalRLogger.reconnecting(attempt, Math.round(delay), previousConnectionId);
            // Interruptible sleep — online/visibility events can resolve reconnectWakeup to skip the wait
            await new Promise<void>(resolve => {
                this.reconnectWakeup = resolve;
                setTimeout(() => { this.reconnectWakeup = null; resolve(); }, delay);
            });

            try {
                // start() requires state === Disconnected, and after a silent socket kill (OS sleep,
                // NAT timeout) onclose may never fire, so force the connection down first.
                if (this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
                    this.signalRLogger.stateChange(this.hubConnection.state as string, 'forcing stop before reconnect');
                    try { await this.hubConnection.stop(); } catch (_) { /* already stopping */ }
                }
                await this.hubConnection.start();
                this.signalRLogger.reconnected(Id, this.hubConnection?.connectionId, previousConnectionId, 'layer2');
                await this.onAfterReconnect(Id);
                return;
            } catch (err: any) {
                this.signalRLogger.error('tryReconnect start() failed', {
                    attempt, groupId: Id, error: err?.message ?? String(err)
                });
                // Log every ~60 attempts (~1 hour at 60s cap) to confirm loop is still alive
                if (attempt > 0 && attempt % 60 === 0) {
                    this.signalRLogger.error('Layer 2 still retrying', {
                        attempt, groupId: Id, minutesElapsed: Math.round(attempt)
                    });
                }
            }
        }
    }

    private async onAfterReconnect(Id: string) {
        const catchUp = ++this.reconnectCatchUpSeq;
        const spread = new Promise<void>(resolve => setTimeout(resolve, Math.random() * RECONNECT_CATCHUP_SPREAD_MS));
        await this.addGroup(Id);
        this.setConnectionState(SignalRConnectionState.Connected);
        await spread;
        // A newer reconnect, or a drop that will reconnect again, owns the catch-up instead.
        if (catchUp !== this.reconnectCatchUpSeq || !this.isConnected) return;
        this.globalService.checkOutDatedData();
    }

    async DisconnectClient() {
        this.signalRLogger.stateChange(this.connectionState$.value, 'disconnecting');
        this.setConnectionState(SignalRConnectionState.Disconnected);
        this.stopFreshnessCheck();
        this.releaseWebLock();
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        if (this.hubConnection) {
            this.hubConnection.off('onClientPortalChanges');
            try {
                await this.hubConnection.stop();
                this.signalRLogger.disconnected('intentional disconnect');
            } catch (err: any) {
                this.signalRLogger.error('DisconnectClient stop() failed', {
                    connectionId: this.hubConnection?.connectionId,
                    error: err?.message ?? String(err)
                });
            }
        }
    }

    // Layer 5: a periodic keepalive catches a silently dead connection (NAT or load balancer
    // idle timeout) where onclose never fires.
    private lastFreshnessCheckAt: number = 0;
    private startFreshnessCheck(): void {
        this.stopFreshnessCheck();
        this.lastFreshnessCheckAt = Date.now();
        this.signalRLogger.stateChange('freshnessCheck', 'started (2 min interval)');
        this.freshnessCheckInterval = setInterval(async () => {
            const timeSinceLastCheck = Date.now() - this.lastFreshnessCheckAt;
            if (this.isConnected && this.hubConnection?.state === signalR.HubConnectionState.Connected) {
                try {
                    await this.invokeWithTimeout('KeepAlive');
                    this.lastFreshnessCheckAt = Date.now();
                    this.signalRLogger.freshnessCheckPassed();
                } catch (err) {
                    this.signalRLogger.freshnessCheckFailed(err);
                    this.signalRLogger.error('freshnessCheck — connection silently dead', {
                        timeSinceLastSuccessMs: timeSinceLastCheck,
                        hubState: this.hubConnection?.state,
                        connectionId: this.hubConnection?.connectionId
                    });
                    // Force reconnect cycle — onclose handler will trigger tryReconnect
                    this.hubConnection.stop();
                }
            } else {
                // State mismatch — our state says connected but hub disagrees, or vice versa
                this.signalRLogger.stateChange('freshnessCheck', `skipped — connState: ${this.connectionState$.value}, hubState: ${this.hubConnection?.state}, timeSinceLastCheckMs: ${timeSinceLastCheck}`);
            }
        }, 2 * 60 * 1000);
    }

    private stopFreshnessCheck(): void {
        if (this.freshnessCheckInterval) {
            clearInterval(this.freshnessCheckInterval);
            this.freshnessCheckInterval = null;
            this.signalRLogger.stateChange('freshnessCheck', 'stopped');
        }
    }

    // A held lock stops Chrome and Safari freezing the WebSocket in a background tab.
    private acquireWebLock(): void {
        if ((navigator as any)?.locks?.request) {
            const promise = new Promise<void>(resolve => { this.webLockResolver = resolve; });
            (navigator as any).locks.request('projul_signalr', { mode: 'shared' }, () => promise);
            this.signalRLogger.stateChange('weblock', 'acquired');
        }
    }

    private releaseWebLock(): void {
        if (this.webLockResolver) {
            this.webLockResolver();
            this.webLockResolver = null;
            this.signalRLogger.stateChange('weblock', 'released');
        }
    }
}

class SignalRConnectionRetryPolicy implements IRetryPolicy {
    constructor(private signalRLogger: SignalRLogger) {}

    nextRetryDelayInMilliseconds(retryContext: signalR.RetryContext): number | null {
        // Layer 1: Retry with exponential backoff for brief blips (~48s total)
        // After 6 attempts, return null to trigger onclose → Layer 2 (tryReconnect)
        const delays = [0, 2000, 5000, 10000, 30000, 30000];
        if (retryContext.previousRetryCount >= delays.length) {
            this.signalRLogger.retryPolicyExhausted(retryContext.previousRetryCount);
            return null;
        }
        const baseDelay = delays[retryContext.previousRetryCount];
        const jitter = Math.random() * 1000;
        this.signalRLogger.retryPolicyDelay(retryContext.previousRetryCount, baseDelay + jitter);
        return baseDelay + jitter;
    }
}
